// novem atom — directory person card (NovemHR-ekranlar port, Phase 8.6).
//
// The selectable person card from the directory grid view
// (emp-directory.jsx): a tinted-when-selected card with an avatar + name
// row up top and a status/identifier footer row below. Promoted out of
// employee-list-content.tsx's DirectoryView, where the grid `personCard`
// was hand-rolled inline.
//
// Slots (all caller-supplied so data + i18n stay in the host):
//   * avatar     — the <Avatar> element.
//   * name       — the person's display name (serif).
//   * nameBadge  — optional trailing badge next to the name ("Ben" / "Me").
//   * subtitle   — secondary line under the name (e.g. work email).
//   * footerStart — left footer slot (e.g. the lifecycle <Badge>).
//   * footerEnd   — right footer slot (e.g. the mono sicil id).
//
// The card IS the interactive element: a real <button> with aria-pressed
// reflecting selection (toggle-button semantics — the original markup
// shipped aria-pressed, preserved verbatim). Native Tab/Enter/Space.
// `selected` drives the visual tint via a data-attribute; onClick is the
// host's selection handler.
//
// Accessibility: the host must give the button an accessible name. The
// visible `name` slot supplies it in the common case; when the name is a
// non-text node the host passes "aria-label" through (…rest).

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface PersonCardProps
  // Omit the native button `name` attribute — this component repurposes
  // `name` as the display-name slot (ReactNode), which shadows it.
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "name"> {
  /** Avatar element (caller supplies <Avatar …/>). */
  readonly avatar: ReactNode;
  /** Display name (serif). */
  readonly name: ReactNode;
  /** Optional badge trailing the name (e.g. a "me" marker). */
  readonly nameBadge?: ReactNode;
  /** Secondary line under the name (e.g. work email). */
  readonly subtitle?: ReactNode;
  /** Left footer slot (e.g. lifecycle badge). */
  readonly footerStart?: ReactNode;
  /** Right footer slot (e.g. mono identifier). */
  readonly footerEnd?: ReactNode;
  /** Selected (tinted) state. Drives aria-pressed + visual tint. */
  readonly selected?: boolean;
}

export const PersonCard = forwardRef<HTMLButtonElement, PersonCardProps>(
  (
    { avatar, name, nameBadge, subtitle, footerStart, footerEnd, selected = false, className, ...rest },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={selected}
      data-component="person-card"
      data-selected={selected || undefined}
      className={cx("nai-person-card", className)}
      {...rest}
    >
      <div className="nai-person-card-head">
        {avatar}
        <div className="nai-person-card-id">
          <div className="nai-person-card-name-row">
            <span className="nai-person-card-name">{name}</span>
            {nameBadge}
          </div>
          {subtitle !== undefined && <div className="nai-person-card-subtitle">{subtitle}</div>}
        </div>
      </div>
      {(footerStart !== undefined || footerEnd !== undefined) && (
        <div className="nai-person-card-footer">
          {footerStart}
          {footerEnd}
        </div>
      )}
    </button>
  ),
);
PersonCard.displayName = "PersonCard";
